import { geoApi } from "../api";

/** Common PIN codes → district & state (offline fallback). */
const STATIC_PIN_MAP: Readonly<Record<string, { district: string; state: string }>> = {
  "110001": { district: "New Delhi", state: "Delhi" },
  "110016": { district: "South Delhi", state: "Delhi" },
  "110092": { district: "East Delhi", state: "Delhi" },
  "122001": { district: "Gurugram", state: "Haryana" },
  "122002": { district: "Gurugram", state: "Haryana" },
  "201301": { district: "Gautam Buddha Nagar", state: "Uttar Pradesh" },
  "201305": { district: "Gautam Buddha Nagar", state: "Uttar Pradesh" },
  "302001": { district: "Jaipur", state: "Rajasthan" },
  "380001": { district: "Ahmedabad", state: "Gujarat" },
  "400001": { district: "Mumbai", state: "Maharashtra" },
  "400051": { district: "Mumbai Suburban", state: "Maharashtra" },
  "400076": { district: "Mumbai Suburban", state: "Maharashtra" },
  "411001": { district: "Pune", state: "Maharashtra" },
  "440001": { district: "Nagpur", state: "Maharashtra" },
  "452001": { district: "Indore", state: "Madhya Pradesh" },
  "500001": { district: "Hyderabad", state: "Telangana" },
  "500032": { district: "Rangareddy", state: "Telangana" },
  "560001": { district: "Bengaluru Urban", state: "Karnataka" },
  "560034": { district: "Bengaluru Urban", state: "Karnataka" },
  "560100": { district: "Bengaluru Urban", state: "Karnataka" },
  "600001": { district: "Chennai", state: "Tamil Nadu" },
  "600028": { district: "Chennai", state: "Tamil Nadu" },
  "641001": { district: "Coimbatore", state: "Tamil Nadu" },
  "682001": { district: "Ernakulam", state: "Kerala" },
  "700001": { district: "Kolkata", state: "West Bengal" },
  "700091": { district: "North 24 Parganas", state: "West Bengal" },
  "751001": { district: "Khordha", state: "Odisha" },
  "800001": { district: "Patna", state: "Bihar" },
  "226001": { district: "Lucknow", state: "Uttar Pradesh" },
  "160001": { district: "Chandigarh", state: "Chandigarh" },
  "403001": { district: "North Goa", state: "Goa" },
};

export type PincodeLookupResult = {
  district: string;
  state: string;
  source: "static" | "api";
};

export function lookupPincodeStatic(pin: string): PincodeLookupResult | null {
  const normalized = pin.replace(/\D/g, "");
  if (normalized.length !== 6) return null;
  const hit = STATIC_PIN_MAP[normalized];
  if (!hit) return null;
  return { ...hit, source: "static" };
};

type PostalApiOffice = {
  District?: string;
  State?: string;
};

type PostalApiResponse = {
  Status?: string;
  PostOffice?: PostalApiOffice[];
};

/** India Post data via public API (no API key). */
export async function lookupPincodeOnline(
  pin: string,
  signal?: AbortSignal,
): Promise<PincodeLookupResult | null> {
  const normalized = pin.replace(/\D/g, "");
  if (normalized.length !== 6) return null;

  const staticHit = lookupPincodeStatic(normalized);
  if (staticHit) return staticHit;

  // Server lookup first: it maps India Post spellings (e.g. "Jammu & Kashmir")
  // onto the app's state list. Fall through to the public API if it is down.
  try {
    const info = await geoApi.pincode(normalized);
    if (signal?.aborted) return null;
    if (info.district && info.state) {
      return { district: info.district, state: info.state_matched ? info.state : "", source: "api" };
    }
  } catch {
    // unauthenticated, offline or upstream error: use the direct lookup below
  }

  try {
    const response = await fetch(`https://api.postalpincode.in/pincode/${normalized}`, { signal });
    if (!response.ok) return null;
    const data = (await response.json()) as PostalApiResponse[];
    const first = data[0];
    if (first?.Status !== "Success" || !first.PostOffice?.length) return null;
    const office = first.PostOffice[0];
    const district = office.District?.trim();
    const state = office.State?.trim();
    if (!district || !state) return null;
    return { district, state, source: "api" };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return null;
    return null;
  }
}

export async function lookupPincode(
  pin: string,
  signal?: AbortSignal,
): Promise<PincodeLookupResult | null> {
  const staticHit = lookupPincodeStatic(pin);
  if (staticHit) return staticHit;
  return lookupPincodeOnline(pin, signal);
}
